// ============================================
// Cliente HTTP — cookie de sessão (credentials: include).
// Substitui o wrapper do Aura (Firebase bearer + X-Aura-Web).
// ============================================

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Disparado quando o servidor responde 401 — o AuthContext escuta e desloga. */
export const UNAUTHORIZED_EVENT = 'raiz:unauthorized'

/**
 * Disparado no 409 company_mismatch (F4): a empresa ativa mudou em OUTRA aba
 * (cookie compartilhado) — o AuthContext re-busca /me e zera os caches em vez
 * de deixar esta aba gravar na empresa errada.
 */
export const COMPANY_MISMATCH_EVENT = 'raiz:company-mismatch'

// Empresa que ESTA aba acredita estar ativa — vira o header X-Company-Id que o
// requireTenant confere contra a sessão (guarda anti-aba-cruzada da F4). Vive em
// módulo (não em contexto) porque o apiFetch é chamado fora da árvore React.
let activeCompanyId: string | null = null
export const setActiveCompanyId = (id: string | null): void => { activeCompanyId = id }

const stringField = (data: unknown, key: string): string | null =>
  data != null && typeof data === 'object' && typeof (data as Record<string, unknown>)[key] === 'string'
    ? (data as Record<string, string>)[key]
    : null

const messageFromStatus = (status: number, data: unknown): string => {
  // Quase todo o servidor responde { error, detail } — o `detail` é a frase
  // para o usuário ("offset fora da faixa", "período máximo de 90 dias"). Só
  // em 4xx: em 5xx o detail pode ser texto interno (err.message, código).
  const serverMessage = stringField(data, 'message') ?? (status < 500 ? stringField(data, 'detail') : null)
  if (serverMessage) return serverMessage
  if (status === 401) return 'Sessão expirada. Faça login novamente.'
  if (status === 403) return 'Sem permissão para esta ação.'
  if (status === 404) return 'Recurso não encontrado.'
  if (status >= 500) return 'Erro no servidor. Tente novamente.'
  return 'Falha na requisição.'
}

export const apiFetch = async <T = unknown>(path: string, options: RequestInit = {}): Promise<T> => {
  let response: Response
  try {
    response = await fetch(path, {
      credentials: 'include',
      ...options,
      headers: {
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(activeCompanyId ? { 'X-Company-Id': activeCompanyId } : {}),
        ...(options.headers ?? {}),
      },
    })
  } catch {
    throw new ApiError(0, 'Sem conexão com o servidor.')
  }

  let data: unknown = null
  const text = await response.text()
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = text
    }
  }

  if (!response.ok) {
    // Só o bootstrap de sessão é isento do auto-logout — /api/auth/users é
    // gestão (V2) e um 401 lá também significa sessão expirada.
    const AUTH_EXEMPT = ['/api/auth/login', '/api/auth/logout', '/api/auth/me']
    if (response.status === 401 && !AUTH_EXEMPT.some((p) => path.startsWith(p))) {
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
    }
    // 409 de tenancy (não qualquer 409): outra aba trocou a empresa ativa.
    if (
      response.status === 409 &&
      data != null && typeof data === 'object' &&
      (data as { error?: unknown }).error === 'company_mismatch'
    ) {
      window.dispatchEvent(new Event(COMPANY_MISMATCH_EVENT))
    }
    throw new ApiError(response.status, messageFromStatus(response.status, data), data)
  }

  return data as T
}

export const apiGet = <T>(path: string) => apiFetch<T>(path)

export const apiPost = <T>(path: string, body?: unknown) =>
  apiFetch<T>(path, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined })

export const apiPatch = <T>(path: string, body: unknown) =>
  apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) })

export const apiPut = <T>(path: string, body: unknown) =>
  apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) })

export const apiDelete = <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' })
